import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { GoogleMap, MarkerF, InfoWindowF, OverlayViewF, OVERLAY_MOUSE_TARGET, useJsApiLoader } from "@react-google-maps/api";
// componentes
import CardServicio from "../cardServicio";
// estilos
import "../../estilos/mapaResultados.css";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const bogota = { lat: 4.711, lng: -74.0721 };

// Estilo oscuro del mapa base (calles/etiquetas apagadas) para que los pines/burbujas de colores
// (verde EnLocal, morado Digital, naranja Domicilio) resalten mucho más en vez de perderse entre
// los íconos y colores por defecto de Google Maps.
const ESTILO_MAPA_OSCURO = [
  { elementType: "geometry", stylers: [{ color: "#1d2c4d" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#8ec3b9" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#1a3646" }] },
  { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [{ color: "#4b6878" }] },
  { featureType: "administrative.land_parcel", elementType: "labels.text.fill", stylers: [{ color: "#64779e" }] },
  { featureType: "administrative.province", elementType: "geometry.stroke", stylers: [{ color: "#4b6878" }] },
  { featureType: "landscape.man_made", elementType: "geometry.stroke", stylers: [{ color: "#334e87" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#023e58" }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#283d6a" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#6f9ba5" }] },
  { featureType: "poi", elementType: "labels.text.stroke", stylers: [{ color: "#1d2c4d" }] },
  // Apaga los íconos de puntos de interés (centros comerciales, hospitales, iglesias, universidades,
  // miradores, etc.) — son del mapa base de Google, no de Zervicy, y competían visualmente con
  // nuestros propios pines/burbujas. Al ser una regla en el featureType genérico "poi", aplica a
  // todos sus subtipos (poi.business, poi.medical, poi.attraction, poi.school...) salvo que haya
  // una regla más específica que la sobreescriba — por eso alcanza con una sola línea.
  { featureType: "poi", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "transit", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "poi.park", elementType: "geometry.fill", stylers: [{ color: "#023e58" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#3C7680" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#304a7d" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#98a5be" }] },
  { featureType: "road", elementType: "labels.text.stroke", stylers: [{ color: "#1d2c4d" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#2c6675" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#255763" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#b0d5ce" }] },
  { featureType: "road.highway", elementType: "labels.text.stroke", stylers: [{ color: "#023e58" }] },
  { featureType: "transit", elementType: "labels.text.fill", stylers: [{ color: "#98a5be" }] },
  { featureType: "transit", elementType: "labels.text.stroke", stylers: [{ color: "#1d2c4d" }] },
  { featureType: "transit.line", elementType: "geometry.fill", stylers: [{ color: "#283d6a" }] },
  { featureType: "transit.station", elementType: "geometry", stylers: [{ color: "#3a4762" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0e1626" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#4e6d70" }] },
];

function obtenerImagenPin(servicio) {
  const arr = servicio?.imagenes;
  if (!Array.isArray(arr)) return null;
  return arr.find(Boolean) || null;
}

// Posiciones en círculo alrededor del centro del mapa para los "flotantes" (Digital/Domicilio).
// No son coordenadas geográficas reales — Digital y Domicilio no tienen una ubicación exacta
// (ver backend/routes/cercaDeMi.js), así que se reparten visualmente alrededor del centro para
// no dejar el mapa vacío en ciudades con pocos EnLocal, en vez de fingir una ubicación que no existe.
function calcularPosicionesEnCirculo(cantidad, radio, anguloInicialGrados) {
  if (!cantidad) return [];
  const paso = 360 / cantidad;

  return Array.from({ length: cantidad }).map((_, i) => {
    const anguloRad = ((anguloInicialGrados + i * paso) * Math.PI) / 180;
    return {
      x: Math.cos(anguloRad) * radio,
      y: Math.sin(anguloRad) * radio,
    };
  });
}

// Burbuja flotante (Digital o Domicilio): foto + borde de color + icono de la categoría, no es un
// pin real del mapa. Se posiciona relativo al div que la envuelve (ver OverlayViewF más abajo, que
// ese sí está anclado a una coordenada geográfica real y se mueve con el pan/zoom del mapa).
function BurbujaFlotante({ servicio, x, y, color, icono, onClick }) {
  const imagen = obtenerImagenPin(servicio);

  return (
    <button
      type="button"
      onClick={onClick}
      title={servicio?.nombre || "Servicio"}
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        transform: `translate(-50%, -50%) translate(${x}px, ${y}px)`,
        width: 46,
        height: 46,
        borderRadius: "50%",
        border: `3px solid ${color}`,
        padding: 0,
        overflow: "visible",
        background: "var(--blanco)",
        boxShadow: "0 2px 8px rgba(var(--negro-rgb), 0.35)",
        cursor: "pointer",
        pointerEvents: "auto",
      }}
    >
      <div style={{ width: "100%", height: "100%", borderRadius: "50%", overflow: "hidden", background: "var(--gris-perla)" }}>
        {imagen ? (
          <img src={imagen} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : null}
      </div>
      <span
        style={{
          position: "absolute",
          bottom: -4,
          right: -4,
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: color,
          color: "var(--blanco)",
          fontSize: 11,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "2px solid var(--blanco)",
        }}
      >
        {icono}
      </span>

      {/* Mismo rojo/ícono que el badge "Urgente" de CardServicio — conecta Urgencia con Cerca de mí
          directamente en el mapa, no solo en el orden de "los mejores" (ver cercaDeMi.js backend). */}
      {servicio?.urgente ? (
        <span
          style={{
            position: "absolute",
            top: -4,
            right: -4,
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: "var(--rojo-error)",
            color: "var(--blanco)",
            fontSize: 11,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "2px solid var(--blanco)",
          }}
          title="Urgente"
        >
          <i className="bi bi-lightning-charge-fill"></i>
        </span>
      ) : null}
    </button>
  );
}

// Vista "Mapa" de /busqueda (y de la sección "Cerca de ti" en Home): pines de servicios EnLocal
// en la ciudad ya filtrada (ver backend/routes/cercaDeMi.js -> /mapa-servicios-cerca). Solo
// lectura, con clustering, distinto del mapa de crearEditar/mapa.jsx (un solo marcador editable).
function MapaResultadosBusqueda({
  servicios,
  serviciosDigitales,
  serviciosDomicilio,
  centro,
  // {lat, lng} explícito de "cerca de mí" en Home — el mismo punto que ya se usó para pedirle al
  // backend los servicios cercanos. A diferencia de `ubicacionNavegador` (que este componente pide
  // por su cuenta solo para el punto azul), este viene del padre y sí gana prioridad para centrar el
  // mapa y anclar las burbujas: "cerca de ti" debería centrarse en dónde estás de verdad, no en el
  // centroide de toda la ciudad. No se usa en /busqueda (ahí no se manda), así que ese flujo no cambia.
  miUbicacion,
  habilitado,
  cargando,
  mensaje,
  ciudad,
  altura = 520,
}) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script-zervicy",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY || "",
  });

  const [ubicacionNavegador, setUbicacionNavegador] = useState(null);
  const [servicioSeleccionado, setServicioSeleccionado] = useState(null);
  // Ventana pequeña (InfoWindow) que aparece al hacer clic en un pin, antes de abrir el panel
  // completo a la derecha. Solo un pin puede tener su ventana abierta a la vez.
  const [pinConInfoAbierta, setPinConInfoAbierta] = useState(null);
  // Pantalla completa: overlay (position:fixed vía portal) que cubre todo debajo del header y
  // bloquea el scroll de la página real detrás — pero adentro del mismo overlay, debajo del mapa
  // y el panel de servicios, se renderiza el Footer real, para que "lo único que se vea" sea
  // header + mapa/panel + footer, y nada más de la página quede visible mientras dure.
  const [pantallaCompleta, setPantallaCompleta] = useState(false);
  const [alturaHeader, setAlturaHeader] = useState(0);

  // Con un servicio seleccionado (fuera de pantalla completa), la card angosta de al lado suele
  // quedar más alta que el mapa de altura fija — en vez de dejar un hueco en blanco debajo del mapa,
  // el mapa se estira (flex) para llenar el mismo alto que termine teniendo la fila completa.
  const mapaFlexible = Boolean(servicioSeleccionado) && !pantallaCompleta;

  const contenedorMapa = {
    width: "100%",
    height: pantallaCompleta ? "100%" : (mapaFlexible ? undefined : `${altura}px`),
    borderRadius: pantallaCompleta ? 0 : "12px",
    overflow: "hidden",
    ...(mapaFlexible ? { flex: "1 1 auto", minHeight: 280 } : {}),
  };

  // Al entrar en pantalla completa: sube el scroll para que el header (no es sticky) quede visible
  // arriba, mide su alto real para no taparlo con el mapa, y bloquea el scroll de la página detrás.
  // Ojo: HeaderGlobal.jsx arma el header en 2 filas (logo/buscador en <header>, categorías debajo
  // como hermano fuera del <header>), ambas dentro de .contenedorHeader — hay que medir ese
  // contenedor completo, si solo medimos <header> la barra de categorías queda tapada por el mapa.
  useEffect(() => {
    if (!pantallaCompleta) return;

    const headerEl = document.querySelector(".contenedorHeader") || document.querySelector("header");
    const medirHeader = () => setAlturaHeader(headerEl?.getBoundingClientRect()?.height || 0);

    window.scrollTo({ top: 0, behavior: "auto" });
    medirHeader();

    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("resize", medirHeader);

    return () => {
      document.body.style.overflow = overflowPrevio;
      window.removeEventListener("resize", medirHeader);
    };
  }, [pantallaCompleta]);

  // Ubicación del navegador, solo local (no se guarda en backend): el mapa debe
  // funcionar también para visitantes anónimos, no solo usuarios logueados.
  useEffect(() => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUbicacionNavegador({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      () => setUbicacionNavegador(null),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  }, []);

  // Prioridad: `miUbicacion` (explícito, solo lo manda Home) gana siempre — "cerca de ti" debe
  // centrarse en dónde estás de verdad, no en el centroide de toda la ciudad. Si no viene (caso de
  // /busqueda), el centro de la ciudad que manda el backend (`centro`) gana sobre la ubicación real
  // del navegador — si no, al elegir una ciudad distinta a donde está físicamente el usuario (dropdown
  // de Búsqueda), el mapa se centraba en su GPS real y la ciudad elegida quedaba fuera de la vista.
  // `ubicacionNavegador` solo sirve de último respaldo, y siempre se sigue mostrando como el punto
  // azul "tu ubicación" (ver más abajo) sin importar esta prioridad.
  const centroMapa = useMemo(() => {
    if (miUbicacion?.lat != null && miUbicacion?.lng != null) return miUbicacion;
    if (centro?.lat != null && centro?.lng != null) return centro;
    if (ubicacionNavegador) return ubicacionNavegador;
    return bogota;
  }, [miUbicacion, ubicacionNavegador, centro]);

  // Límite de la ciudad: se puede seguir haciendo zoom, pero no arrastrar el mapa fuera de esta caja.
  // Se ancla al centroide de la ciudad que manda el backend (centro), no a la ubicación del usuario —
  // así el límite no se corre si el GPS del usuario queda un poco descentrado dentro del área metro.
  // El delta (~0.3°, ~33km) cubre con margen los municipios del área metropolitana donde sí hay
  // servicios reales (ej. Funza, Mosquera, Chía, Soacha alrededor de Bogotá, a 18-25km del centro),
  // pero corta antes de pueblos ya lejanos (Zipaquirá, Sopó, Guasca) que no aportan al "cerca de mí".
  const limiteCiudad = useMemo(() => {
    if (centro?.lat == null || centro?.lng == null) return null;

    const DELTA_GRADOS = 0.3;
    return {
      north: centro.lat + DELTA_GRADOS,
      south: centro.lat - DELTA_GRADOS,
      east: centro.lng + DELTA_GRADOS,
      west: centro.lng - DELTA_GRADOS,
    };
  }, [centro]);

  const pines = useMemo(() => {
    return (servicios || [])
      .filter((s) => s?.ubicacion?.latitud != null && s?.ubicacion?.longitud != null)
      .map((s) => ({
        servicio: s,
        position: { lat: Number(s.ubicacion.latitud), lng: Number(s.ubicacion.longitud) },
      }));
  }, [servicios]);

  const burbujasDigitales = useMemo(() => {
    const lista = Array.isArray(serviciosDigitales) ? serviciosDigitales : [];
    const posiciones = calcularPosicionesEnCirculo(lista.length, 78, -90);
    return lista.map((s, i) => ({ servicio: s, ...posiciones[i] }));
  }, [serviciosDigitales]);

  const burbujasDomicilio = useMemo(() => {
    const lista = Array.isArray(serviciosDomicilio) ? serviciosDomicilio : [];
    const posiciones = calcularPosicionesEnCirculo(lista.length, 138, -90 + 36);
    return lista.map((s, i) => ({ servicio: s, ...posiciones[i] }));
  }, [serviciosDomicilio]);

  // Todos los servicios visibles en el mapa (pines EnLocal + flotantes Digital/Domicilio) juntos,
  // para la cuadrícula del panel en pantalla completa (ver más abajo).
  const todosLosServiciosPanel = useMemo(() => ([
    ...(servicios || []),
    ...(serviciosDigitales || []),
    ...(serviciosDomicilio || []),
  ]), [servicios, serviciosDigitales, serviciosDomicilio]);

  // Vuelve a colapsar el panel y la ventana pequeña si cambia la lista de pines (nueva búsqueda/ciudad).
  useEffect(() => {
    setServicioSeleccionado(null);
    setPinConInfoAbierta(null);
  }, [servicios]);

  // Al hacer clic en un pin/burbuja, el panel de pantalla completa hace scroll hasta esa tarjeta
  // y la resalta (en vez de reordenar la cuadrícula, que se sentiría inestable con cada clic).
  useEffect(() => {
    if (!pantallaCompleta || !servicioSeleccionado?.idServicio) return;

    const nodo = document.getElementById(`panel-servicio-${servicioSeleccionado.idServicio}`);
    nodo?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [pantallaCompleta, servicioSeleccionado]);

  if (!ciudad) {
    return <div className="alert alert-info mb-0">Escribe una ciudad para ver el mapa de servicios cercanos.</div>;
  }

  if (cargando) {
    return <div className="text-muted">Cargando mapa...</div>;
  }

  if (!habilitado) {
    return (
      <div className="text-center py-4 px-3" style={{ background: "var(--gris-fondo-alt)", borderRadius: 16 }}>
        <div className="mb-2" style={{ fontSize: 26 }}>
          <i className="bi bi-geo-alt text-primary" aria-hidden="true"></i>
        </div>
        <div className="fw-semibold mb-1">
          {mensaje || "Tu ciudad aún no tiene suficientes servicios para mostrar el mapa."}
        </div>
        <div className="text-muted" style={{ fontSize: 14 }}>
          Sigue explorando los resultados mientras se suman más proveedores por aquí.
        </div>
      </div>
    );
  }

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="alert alert-warning mb-0">
        Falta configurar la variable <strong>REACT_APP_GOOGLE_MAPS_API_KEY</strong> para cargar Google Maps.
      </div>
    );
  }

  if (loadError) {
    return <div className="alert alert-danger mb-0">No fue posible cargar Google Maps.</div>;
  }

  if (!isLoaded) {
    return <div className="text-muted">Cargando mapa...</div>;
  }

  // Contenido del mapa (Google Maps + burbujas flotantes), reutilizado igual en la vista normal
  // y en pantalla completa — lo único que cambia entre las dos es el wrapper de afuera.
  const nodoMapaGoogle = (
    <div style={{ ...contenedorMapa, position: "relative" }}>
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={centroMapa}
            // Un poco más de zoom cuando centramos en tu punto exacto (miUbicacion) — a nivel de
            // ciudad completa (12) tu ubicación puntual se ve demasiado alejada.
            zoom={miUbicacion ? 14 : 12}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: true,
              // Evita que se abran las fichas nativas de Google (iglesias, centros comerciales,
              // negocios que ya trae el mapa base) al hacer clic — solo deben responder nuestros
              // propios pines/burbujas de servicios de Zervicy.
              clickableIcons: false,
              styles: ESTILO_MAPA_OSCURO,
              // Deja hacer zoom libremente, pero no arrastrar el mapa fuera de la ciudad.
              ...(limiteCiudad ? { restriction: { latLngBounds: limiteCiudad, strictBounds: false } } : {}),
            }}
          >
            {ubicacionNavegador && (
              <MarkerF
                position={ubicacionNavegador}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 8,
                  fillColor: "#1a73e8",
                  fillOpacity: 1,
                  strokeColor: "#ffffff",
                  strokeWeight: 2,
                }}
                title="Tu ubicación"
              />
            )}

            {/* Pines EnLocal: igual que las burbujas de Digital/Domicilio, círculo con la foto real
                del servicio (reutilizamos BurbujaFlotante con x=0/y=0 porque cada uno ya tiene su
                propia coordenada geográfica exacta, no necesita repartirse en círculo alrededor de
                un centro). Cada uno en su propio OverlayViewF — a diferencia de un Marker normal,
                no se agrupan automáticamente en clusters con número si hay muchos muy juntos; con
                el volumen actual no hace falta, pero si una ciudad llega a tener muchos EnLocal muy
                cerca unos de otros vale la pena revisar esto de nuevo. */}
            {pines.map((pin) => (
              <OverlayViewF key={pin.servicio.idServicio} position={pin.position} mapPaneName={OVERLAY_MOUSE_TARGET}>
                <BurbujaFlotante
                  servicio={pin.servicio}
                  x={0}
                  y={0}
                  color="var(--verde-mapa)"
                  icono="📍"
                  onClick={() => setPinConInfoAbierta(pin)}
                />
              </OverlayViewF>
            ))}

            {pinConInfoAbierta ? (
              <InfoWindowF
                position={pinConInfoAbierta.position}
                onCloseClick={() => setPinConInfoAbierta(null)}
              >
                <div
                  role="button"
                  onClick={() => {
                    setServicioSeleccionado(pinConInfoAbierta.servicio);
                    setPinConInfoAbierta(null);
                  }}
                  style={{ width: 150, cursor: "pointer" }}
                >
                  <div
                    style={{
                      width: "100%",
                      height: 90,
                      borderRadius: 8,
                      overflow: "hidden",
                      background: "var(--gris-perla)",
                      marginBottom: 6,
                    }}
                  >
                    {obtenerImagenPin(pinConInfoAbierta.servicio) ? (
                      <img
                        src={obtenerImagenPin(pinConInfoAbierta.servicio)}
                        alt={pinConInfoAbierta.servicio?.nombre || "Servicio"}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : null}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2 }}>
                    {pinConInfoAbierta.servicio?.nombre || "Servicio"}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--azul-bootstrap-cta)", marginTop: 2 }}>Ver más</div>
                </div>
              </InfoWindowF>
            ) : null}

            {/* Flotantes: Digital y Domicilio no tienen coordenada real (ver cercaDeMi.js), así que
                no son pines del mapa — pero se anclan con OverlayView a la misma coordenada donde
                centramos el mapa (tu ubicación si la diste, si no el centro de la ciudad), para que
                se muevan junto con el pan/zoom en vez de quedarse fijos en la pantalla. */}
            {(burbujasDigitales.length > 0 || burbujasDomicilio.length > 0) && (
              <OverlayViewF position={centroMapa} mapPaneName={OVERLAY_MOUSE_TARGET}>
                <div style={{ position: "relative" }}>
                  {burbujasDigitales.map((b) => (
                    <BurbujaFlotante
                      key={b.servicio.idServicio}
                      servicio={b.servicio}
                      x={b.x}
                      y={b.y}
                      color="var(--violeta-mapa)"
                      icono="💻"
                      onClick={() => setServicioSeleccionado(b.servicio)}
                    />
                  ))}
                  {burbujasDomicilio.map((b) => (
                    <BurbujaFlotante
                      key={b.servicio.idServicio}
                      servicio={b.servicio}
                      x={b.x}
                      y={b.y}
                      color="var(--naranja-mapa)"
                      icono="🚚"
                      onClick={() => setServicioSeleccionado(b.servicio)}
                    />
                  ))}
                </div>
              </OverlayViewF>
            )}
          </GoogleMap>

          {/* Botón pantalla completa / salir, flotando sobre la esquina del mapa. */}
          <button
            type="button"
            className="boton-mapa-fullscreen"
            onClick={() => setPantallaCompleta((v) => !v)}
            style={{ position: "absolute", top: 12, right: 12, zIndex: 5 }}
            title={pantallaCompleta ? "Salir de pantalla completa" : "Ver en pantalla completa"}
          >
            <i className={`bi ${pantallaCompleta ? "bi-fullscreen-exit" : "bi-arrows-fullscreen"}`}></i>
          </button>
    </div>
  );

  const leyenda = (
    <div className="d-flex flex-wrap align-items-center gap-3 mt-2">
      <p className="textoBajoInput mb-0">
        {todosLosServiciosPanel.length} {todosLosServiciosPanel.length === 1 ? "servicio" : "servicios"} en el mapa. Haz clic en un pin o una burbuja para ver el detalle.
      </p>
      {(pines.length > 0 || burbujasDigitales.length > 0 || burbujasDomicilio.length > 0) ? (
        <div className="d-flex align-items-center gap-3" style={{ fontSize: 12 }}>
          {pines.length > 0 ? (
            <span className="d-flex align-items-center gap-1">
              <span style={{ width: 10, height: 10, borderRadius: "50%", background: "var(--verde-mapa)", display: "inline-block" }} />
              Locales
            </span>
          ) : null}
          {burbujasDigitales.length > 0 ? (
            <span className="d-flex align-items-center gap-1">
              <span style={{ width: 10, height: 10, borderRadius: "50%", background: "var(--violeta-mapa)", display: "inline-block" }} />
              Digital
            </span>
          ) : null}
          {burbujasDomicilio.length > 0 ? (
            <span className="d-flex align-items-center gap-1">
              <span style={{ width: 10, height: 10, borderRadius: "50%", background: "var(--naranja-mapa)", display: "inline-block" }} />
              A domicilio
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  const panelServicio = servicioSeleccionado ? (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="fw-semibold" style={{ fontSize: 14 }}>Servicio seleccionado</span>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setServicioSeleccionado(null)}>
          Cerrar
        </button>
      </div>
      <div className="pb-3">
        <CardServicio servicio={servicioSeleccionado} clampDesc={4} textoBoton="Ver servicio" desdeDonde="busqueda" />
      </div>
    </>
  ) : null;

  // PANTALLA COMPLETA: overlay fijo (portal a document.body) que cubre todo debajo del header y
  // bloquea el scroll de la página real detrás. Siempre abre en split 70% mapa / 30% servicios con
  // una cuadrícula de 2 columnas de todos los servicios visibles (pines + flotantes); al hacer clic
  // en un pin/burbuja, esa tarjeta se resalta y el panel hace scroll hasta ella.
  if (pantallaCompleta) {
    return createPortal(
      <div
        className="mapa-pantalla-completa"
        style={{
          position: "fixed",
          top: alturaHeader,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1040,
          background: "var(--blanco)",
        }}
      >
        <div className="mapa-pantalla-completa-mapa">
          {nodoMapaGoogle}
        </div>

        <div className="mapa-pantalla-completa-panel">
          <div className="fw-semibold mb-2" style={{ fontSize: 14 }}>
            {todosLosServiciosPanel.length} {todosLosServiciosPanel.length === 1 ? "servicio" : "servicios"}
          </div>

          {todosLosServiciosPanel.length === 0 ? (
            <div className="text-muted" style={{ fontSize: 13 }}>No hay servicios para mostrar todavía.</div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12, maxWidth: 640 }}>
              {todosLosServiciosPanel.map((s) => {
                const esSeleccionado = servicioSeleccionado?.idServicio === s.idServicio;
                return (
                  <div
                    key={s.idServicio}
                    id={`panel-servicio-${s.idServicio}`}
                    onClick={() => setServicioSeleccionado(s)}
                    style={{
                      cursor: "pointer",
                      borderRadius: 16,
                      minWidth: 0,
                      maxWidth: 280,
                      overflow: "hidden",
                      border: esSeleccionado ? "2px solid var(--azul-bootstrap-cta)" : "2px solid transparent",
                      transition: "border-color 0.2s ease",
                    }}
                  >
                    <CardServicio servicio={s} clampDesc={2} textoBoton="Ver servicio" desdeDonde="busqueda" />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>,
      document.body
    );
  }

  return (
    <div className="row g-3">
      <div className={servicioSeleccionado ? "col-12 col-lg-9 d-flex flex-column" : "col-12"}>
        {nodoMapaGoogle}
        {leyenda}
      </div>

      {servicioSeleccionado ? (
        <div className="col-12 col-lg-3">
          {panelServicio}
        </div>
      ) : null}
    </div>
  );
}

export default MapaResultadosBusqueda;
